/**
 * Wire-контракт службы синхронизации (`src/Sync/Sync.Contracts`, RP-SYN1) и шаблона создания (`Resource.Contracts.SyncCreateTemplate`) и
 * его проверка на границе: неожиданная форма — `invalid-response`, а не пустой список. Имена полей — camelCase сериализации .NET.
 */
import { fail, flag, integer, list, oneOf, optionalInteger, optionalNumber, optionalText, record, text } from '../../../shared/decode'
import {
  CHANGE_TYPES,
  CHECK_STATES,
  CHECK_TRIGGERS,
  DATA_KINDS,
  FIELD_TYPES,
  IGNORE_LEVELS,
  JOB_ITEM_ACTIONS,
  JOB_ITEM_STATES,
  JOB_STATES,
  MATCH_KINDS,
  type ApplyJob,
  type CheckRun,
  type CreateTemplate,
  type Divergence,
  type DivergenceCount,
  type DivergencesPage,
  type Ignore,
  type InitProposal,
  type InitResult,
  type JournalPage,
  type PriceMapsView,
  type PriceTypeMap,
  type Schedule,
  type SourcesSummary,
} from '../model/types'

const strings = (value: unknown, field: string): string[] => list(value, field, (entry, name) => text(entry, name))

/** Число `long` .NET: целое, иначе ответ не по договору. */
function id(value: unknown, field: string): number {
  const number = integer(value, field)
  return Number.isSafeInteger(number) ? number : fail(`поле ${field} — не целое`)
}

export function decodeCheckRun(raw: unknown, name = 'check'): CheckRun {
  const item = record(raw, name)
  return {
    id: text(item.id, `${name}.id`),
    resourceId: text(item.resourceId, `${name}.resourceId`),
    trigger: oneOf(item.trigger, CHECK_TRIGGERS, `${name}.trigger`),
    state: oneOf(item.state, CHECK_STATES, `${name}.state`),
    read: integer(item.read, `${name}.read`),
    total: optionalInteger(item.total, `${name}.total`),
    errorCode: optionalText(item.errorCode, `${name}.errorCode`),
    errorMessage: optionalText(item.errorMessage, `${name}.errorMessage`),
    startedAt: text(item.startedAt, `${name}.startedAt`),
    finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
  }
}

export function decodeSummary(raw: unknown): SourcesSummary {
  const body = record(raw, 'summary')
  const common = record(body.commonCatalog, 'summary.commonCatalog')
  return {
    commonCatalog: {
      productCount: integer(common.productCount, 'summary.commonCatalog.productCount'),
      onlyCommonCount: integer(common.onlyCommonCount, 'summary.commonCatalog.onlyCommonCount'),
      resourceId: text(common.resourceId, 'summary.commonCatalog.resourceId'),
    },
    sources: list(body.sources, 'summary.sources', (entry, name) => {
      const item = record(entry, name)
      return {
        resourceId: text(item.resourceId, `${name}.resourceId`),
        kind: text(item.kind, `${name}.kind`),
        displayName: text(item.displayName, `${name}.displayName`),
        itemCount: integer(item.itemCount, `${name}.itemCount`),
        barcodeCount: integer(item.barcodeCount, `${name}.barcodeCount`),
        divergenceCount: integer(item.divergenceCount, `${name}.divergenceCount`),
        lastCheck: item.lastCheck === null || item.lastCheck === undefined ? null : decodeCheckRun(item.lastCheck, `${name}.lastCheck`),
      }
    }),
  }
}

function decodeDivergence(raw: unknown, name: string): Divergence {
  const item = record(raw, name)
  return {
    id: id(item.id, `${name}.id`),
    type: oneOf(item.type, CHANGE_TYPES, `${name}.type`),
    dataKind: oneOf(item.dataKind, DATA_KINDS, `${name}.dataKind`),
    productId: optionalText(item.productId, `${name}.productId`),
    originResourceId: optionalText(item.originResourceId, `${name}.originResourceId`),
    field: optionalText(item.field, `${name}.field`),
    subject: optionalText(item.subject, `${name}.subject`),
    title: text(item.title, `${name}.title`),
    values: list(item.values, `${name}.values`, (entry, field) => {
      const value = record(entry, field)
      return {
        resourceId: text(value.resourceId, `${field}.resourceId`),
        remoteKey: optionalText(value.remoteKey, `${field}.remoteKey`),
        present: flag(value.present, `${field}.present`),
        value: optionalText(value.value, `${field}.value`),
        priceTypeKey: optionalText(value.priceTypeKey, `${field}.priceTypeKey`),
        priceTypeName: optionalText(value.priceTypeName, `${field}.priceTypeName`),
        vatPercent: optionalNumber(value.vatPercent, `${field}.vatPercent`),
      }
    }),
    detectedAt: text(item.detectedAt, `${name}.detectedAt`),
  }
}

export function decodeDivergences(raw: unknown): DivergencesPage {
  const body = record(raw, 'divergences')
  return {
    items: list(body.items, 'divergences.items', decodeDivergence),
    total: integer(body.total, 'divergences.total'),
    page: integer(body.page, 'divergences.page'),
    pageSize: integer(body.pageSize, 'divergences.pageSize'),
  }
}

export function decodeCount(raw: unknown): DivergenceCount {
  const body = record(raw, 'count')
  return { total: integer(body.total, 'count.total') }
}

export function decodeTemplate(raw: unknown): CreateTemplate {
  const body = record(raw, 'template')
  return {
    resourceId: text(body.resourceId, 'template.resourceId'),
    fields: list(body.fields, 'template.fields', (entry, name) => {
      const item = record(entry, name)
      return {
        key: text(item.key, `${name}.key`),
        label: text(item.label, `${name}.label`),
        type: oneOf(item.type, FIELD_TYPES, `${name}.type`),
        required: flag(item.required, `${name}.required`),
        options:
          item.options === null || item.options === undefined
            ? []
            : list(item.options, `${name}.options`, (option, field) => {
                const value = record(option, field)
                return { value: text(value.value, `${field}.value`), label: text(value.label, `${field}.label`) }
              }),
        defaultValue: optionalText(item.defaultValue, `${name}.defaultValue`),
      }
    }),
  }
}

export function decodeJob(raw: unknown): ApplyJob {
  const body = record(raw, 'job')
  return {
    id: text(body.id, 'job.id'),
    state: oneOf(body.state, JOB_STATES, 'job.state'),
    createdBy: text(body.createdBy, 'job.createdBy'),
    createdByName: optionalText(body.createdByName, 'job.createdByName'),
    total: integer(body.total, 'job.total'),
    done: integer(body.done, 'job.done'),
    failed: integer(body.failed, 'job.failed'),
    errorMessage: optionalText(body.errorMessage, 'job.errorMessage'),
    createdAt: text(body.createdAt, 'job.createdAt'),
    startedAt: optionalText(body.startedAt, 'job.startedAt'),
    finishedAt: optionalText(body.finishedAt, 'job.finishedAt'),
    items: list(body.items, 'job.items', (entry, name) => {
      const item = record(entry, name)
      const divergenceId = item.divergenceId === null || item.divergenceId === undefined ? null : id(item.divergenceId, `${name}.divergenceId`)
      return {
        id: id(item.id, `${name}.id`),
        divergenceId,
        dataKind: oneOf(item.dataKind, DATA_KINDS, `${name}.dataKind`),
        action: oneOf(item.action, JOB_ITEM_ACTIONS, `${name}.action`),
        sourceResourceId: optionalText(item.sourceResourceId, `${name}.sourceResourceId`),
        targetResourceId: text(item.targetResourceId, `${name}.targetResourceId`),
        remoteKey: optionalText(item.remoteKey, `${name}.remoteKey`),
        title: text(item.title, `${name}.title`),
        state: oneOf(item.state, JOB_ITEM_STATES, `${name}.state`),
        errorCode: optionalText(item.errorCode, `${name}.errorCode`),
        message: optionalText(item.message, `${name}.message`),
        requestId: optionalText(item.requestId, `${name}.requestId`),
        finishedAt: optionalText(item.finishedAt, `${name}.finishedAt`),
      }
    }),
  }
}

export function decodeIgnore(raw: unknown, name = 'ignore'): Ignore {
  const item = record(raw, name)
  return {
    id: id(item.id, `${name}.id`),
    level: oneOf(item.level, IGNORE_LEVELS, `${name}.level`),
    resourceId: text(item.resourceId, `${name}.resourceId`),
    dataKind: oneOf(item.dataKind, DATA_KINDS, `${name}.dataKind`),
    remoteKey: optionalText(item.remoteKey, `${name}.remoteKey`),
    productId: optionalText(item.productId, `${name}.productId`),
    subject: optionalText(item.subject, `${name}.subject`),
    createdBy: text(item.createdBy, `${name}.createdBy`),
    createdAt: text(item.createdAt, `${name}.createdAt`),
  }
}

export function decodeIgnores(raw: unknown): Ignore[] {
  const body = record(raw, 'ignores')
  return list(body.items, 'ignores.items', decodeIgnore)
}

export function decodeInit(raw: unknown): InitProposal {
  const body = record(raw, 'init')
  return {
    resourceId: text(body.resourceId, 'init.resourceId'),
    candidates: list(body.candidates, 'init.candidates', (entry, name) => {
      const item = record(entry, name)
      return {
        productId: text(item.productId, `${name}.productId`),
        productName: text(item.productName, `${name}.productName`),
        remoteKey: text(item.remoteKey, `${name}.remoteKey`),
        name: text(item.name, `${name}.name`),
        matchedBy: oneOf(item.matchedBy, MATCH_KINDS, `${name}.matchedBy`),
      }
    }),
    ambiguous: list(body.ambiguous, 'init.ambiguous', (entry, name) => {
      const item = record(entry, name)
      return {
        matchedBy: oneOf(item.matchedBy, MATCH_KINDS, `${name}.matchedBy`),
        value: text(item.value, `${name}.value`),
        remoteKeys: strings(item.remoteKeys, `${name}.remoteKeys`),
        productIds: strings(item.productIds, `${name}.productIds`),
      }
    }),
  }
}

export function decodeInitResult(raw: unknown): InitResult {
  const body = record(raw, 'initResult')
  return {
    created: integer(body.created, 'initResult.created'),
    alreadyLinked: integer(body.alreadyLinked, 'initResult.alreadyLinked'),
    failed: list(body.failed, 'initResult.failed', (entry, name) => {
      const item = record(entry, name)
      return {
        productId: text(item.productId, `${name}.productId`),
        remoteKey: text(item.remoteKey, `${name}.remoteKey`),
        message: text(item.message, `${name}.message`),
      }
    }),
  }
}

function decodePriceMap(raw: unknown, name: string): PriceTypeMap {
  const item = record(raw, name)
  return {
    firstResourceId: text(item.firstResourceId, `${name}.firstResourceId`),
    firstPriceTypeKey: text(item.firstPriceTypeKey, `${name}.firstPriceTypeKey`),
    secondResourceId: text(item.secondResourceId, `${name}.secondResourceId`),
    secondPriceTypeKey: text(item.secondPriceTypeKey, `${name}.secondPriceTypeKey`),
  }
}

export function decodePriceMaps(raw: unknown): PriceMapsView {
  const body = record(raw, 'priceTypeMaps')
  return {
    items: list(body.items, 'priceTypeMaps.items', decodePriceMap),
    sources: list(body.sources, 'priceTypeMaps.sources', (entry, name) => {
      const source = record(entry, name)
      return {
        resourceId: text(source.resourceId, `${name}.resourceId`),
        priceTypes: list(source.priceTypes, `${name}.priceTypes`, (type, field) => {
          const item = record(type, field)
          return { key: text(item.key, `${field}.key`), name: optionalText(item.name, `${field}.name`) }
        }),
      }
    }),
    suggestions: list(body.suggestions, 'priceTypeMaps.suggestions', decodePriceMap),
  }
}

export function decodeSchedule(raw: unknown): Schedule {
  const body = record(raw, 'schedule')
  const nightlyAt = text(body.nightlyAt, 'schedule.nightlyAt')
  return {
    nightlyAt: /^\d{2}:\d{2}$/.test(nightlyAt) ? nightlyAt : fail('поле schedule.nightlyAt — не «ЧЧ:ММ»'),
    timeZone: text(body.timeZone, 'schedule.timeZone'),
  }
}

export function decodeJournal(raw: unknown): JournalPage {
  const body = record(raw, 'journal')
  return {
    items: list(body.items, 'journal.items', (entry, name) => {
      const item = record(entry, name)
      return {
        jobId: text(item.jobId, `${name}.jobId`),
        itemId: id(item.itemId, `${name}.itemId`),
        actor: text(item.actor, `${name}.actor`),
        actorName: optionalText(item.actorName, `${name}.actorName`),
        at: text(item.at, `${name}.at`),
        dataKind: oneOf(item.dataKind, DATA_KINDS, `${name}.dataKind`),
        action: oneOf(item.action, JOB_ITEM_ACTIONS, `${name}.action`),
        sourceResourceId: optionalText(item.sourceResourceId, `${name}.sourceResourceId`),
        targetResourceId: text(item.targetResourceId, `${name}.targetResourceId`),
        title: text(item.title, `${name}.title`),
        state: oneOf(item.state, JOB_ITEM_STATES, `${name}.state`),
        message: optionalText(item.message, `${name}.message`),
      }
    }),
    total: integer(body.total, 'journal.total'),
    page: integer(body.page, 'journal.page'),
    pageSize: integer(body.pageSize, 'journal.pageSize'),
  }
}
